import api from '@shared/services/api';
import {
  CreateQuestionBankItemRequest,
  QuestionBankItem,
  UpdateQuestionBankItemRequest,
} from '../types/question-bank.types';

/**
 * Service for managing question bank items
 */
export class QuestionBankService {
  private static readonly BASE_URL = `/question-bank/items`;

  /**
   * Get all question bank items with pagination support
   */
  static async getQuestionBankItems(
    page = 1,
    limit = 10
  ): Promise<{ items: QuestionBankItem[]; total: number }> {
    try {
      const response = await api.get(this.BASE_URL, {
        params: { page, limit },
      });
      return {
        items: response.data.items,
        total: response.data.total,
      };
    } catch (error) {
      console.error('Error fetching question bank items:', error);
      throw error;
    }
  }

  /**
   * Get question bank item by ID
   */
  static async getQuestionBankItemById(id: string): Promise<QuestionBankItem> {
    try {
      const response = await api.get(`${this.BASE_URL}/${id}`);
      return response.data.item;
    } catch (error) {
      console.error(`Error fetching question bank item with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new question bank item
   */
  static async createQuestionBankItem(
    data: CreateQuestionBankItemRequest
  ): Promise<QuestionBankItem> {
    try {
      const response = await api.post(this.BASE_URL, data);
      return response.data.item;
    } catch (error) {
      console.error('Error creating question bank item:', error);
      throw error;
    }
  }

  /**
   * Update question bank item
   */
  static async updateQuestionBankItem(
    id: string,
    data: UpdateQuestionBankItemRequest
  ): Promise<QuestionBankItem> {
    try {
      const response = await api.put(`${this.BASE_URL}/${id}`, data);
      return response.data.item;
    } catch (error) {
      console.error(`Error updating question bank item with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete question bank item
   */
  static async deleteQuestionBankItem(id: string): Promise<void> {
    try {
      await api.delete(`${this.BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Error deleting question bank item with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all categories
   */
  static async getCategories(): Promise<string[]> {
    try {
      const response = await api.get(`${this.BASE_URL}/categories`);
      return response.data.categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }
}

// Export individual functions for backward compatibility
export const getQuestionBankItems =
  QuestionBankService.getQuestionBankItems.bind(QuestionBankService);
export const getQuestionBankItemById =
  QuestionBankService.getQuestionBankItemById.bind(QuestionBankService);
export const createQuestionBankItem =
  QuestionBankService.createQuestionBankItem.bind(QuestionBankService);
export const updateQuestionBankItem =
  QuestionBankService.updateQuestionBankItem.bind(QuestionBankService);
export const deleteQuestionBankItem =
  QuestionBankService.deleteQuestionBankItem.bind(QuestionBankService);
export const getCategories =
  QuestionBankService.getCategories.bind(QuestionBankService);
